import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import TaskTemplateFilter from '@/modules/tasks/TaskTemplateFilter';
import Checkbox from '@/shared/components/input/Checkbox';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import classNames from 'classnames';
import authService, { privileges } from '@/shared/services/AuthService';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import handleHttpError from '../../../shared/services/HandleHttpError';
import messages from './workflowConfigurationMessages';

class WorkflowConfigurationModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    workflowConfiguration: PropTypes.shape({}).isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    show: false,
  };

  constructor(props) {
    super(props);
    const { workflowConfiguration } = this.props;

    this.state = {
      viewModel: workflowConfiguration || {
        workflowType: null,
        desiredStatus: '',
        mainTaskTemplateId: null,
        subTaskTemplateId: null,
        toleranceDaysAfter: 0,
        toleranceDaysBefore: 0,
        requireDocumentSigned: false,
      },
      showValidation: false,
      workflowTypes: [],
    };
  }

  componentDidMount() {
    const { intl } = this.props;
    const { viewModel } = this.state;
    enumCache.get('workflowType').then(data => {
      let selectedWorkflow;
      const mappedWorkflows = data
        .map(x => ({
          id: x.key,
          label: intl.formatMessage(listItemMsg('workflowType', x.key, x.value)),
        }))
        .sort(dynamicSort('label'));
      if (viewModel.workflowType >= 0) {
        selectedWorkflow = mappedWorkflows
          .map(x => ({ key: x.id, label: x.label }))
          .find(x => x.key === viewModel.workflowType);
      }
      this.setState({
        selectedWorkflow,
        workflowTypes: mappedWorkflows,
      });
    });
  }

  closeModal = () => {
    const { onClose } = this.props;

    onClose();
  };

  saveModal = () => {
    const { onSave, intl } = this.props;
    const { viewModel } = this.state;
    const configurationDto = { ...viewModel };
    const successMessage = intl.formatMessage(messages.ChangesSaved);

    if (configurationDto.workflowType === 3) {
      configurationDto.desiredStatus = null; // remove desired status for VAT amount workflow
    }
    // Validate
    if (
      !configurationDto.mainTaskTemplateId ||
      (!viewModel.workflowType && viewModel.workflowType !== 0)
    ) {
      this.setState(state => ({
        ...state,
        showValidation: true,
      }));
      return;
    }

    const action = configurationDto.id === undefined ? api.base.post : api.base.put;

    return action(`${config.appUrls.api}/api/workflowconfiguration`, configurationDto).then(
      ({ data: serviceObject }) => {
        ToastrService.success(successMessage);
        onSave(serviceObject);
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  selectedWorkflowChanged = workflow => {
    if (workflow === undefined) return;
    const { viewModel } = this.state;
    this.setState({
      selectedWorkflow: workflow,
      viewModel: {
        ...viewModel,
        workflowType: workflow ? parseInt(workflow.key) : undefined,
      },
    });
  };

  render() {
    const { show, intl } = this.props;
    const { viewModel, showValidation, workflowTypes, selectedWorkflow } = this.state;
    const showSubtaskDetails = !viewModel.workflowType || viewModel.workflowType !== 3;
    const showReferenceDate = !viewModel.workflowType || viewModel.workflowType !== 58;
    const workFlowTypeClassName = classNames('form-group', {
      'has-error': showValidation && !viewModel.workflowType && viewModel.workflowType !== 0,
    });

    const canUpdate = authService.hasPermission({
      functionality: 'settings',
      privilege: privileges.Update,
    });

    const mappedWorkflows = workflowTypes.map(x => ({
      key: x.id,
      label: x.label,
    }));

    const isPitMandatesTaxReturnTypeTaskflow = selectedWorkflow?.key === 144;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.TaskflowAction)}
        show={show}
        disabled={!canUpdate}
        onClose={this.closeModal}
        onSave={this.saveModal}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div className={workFlowTypeClassName}>
                <label className="control-label col-md-4">
                  {intl.formatMessage(messages.WorkflowType)}
                </label>
                <div className="col-md-8">
                  <AutoComplete
                    items={mappedWorkflows}
                    placeholder={intl.formatMessage(messages.StartTypingToSearch)}
                    onChange={item => this.selectedWorkflowChanged(item)}
                    mode="edit"
                    value={selectedWorkflow}
                    showAll
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-md-4">
                  {intl.formatMessage(messages.RequireDocumentSigned)}
                </label>
                <div className="col-md-8 margin5T">
                  <Checkbox
                    onClick={value =>
                      this.setState({
                        viewModel: {
                          ...viewModel,
                          requireDocumentSigned: value,
                        },
                      })
                    }
                    value={viewModel.requireDocumentSigned}
                  />
                </div>
              </div>
              <hr />
              {showSubtaskDetails &&
                !isPitMandatesTaxReturnTypeTaskflow &&
                viewModel.mainTaskTemplateId &&
                !viewModel.subTaskTemplateId && (
                  <div className="form-group">
                    <div className="col-md-offset-4 col-md-8">
                      {intl.formatMessage(messages.AllSubtasksWillBeSetToDoneIfYouDontSelectOne)}
                    </div>
                  </div>
                )}
              {isPitMandatesTaxReturnTypeTaskflow &&
                viewModel.mainTaskTemplateId &&
                !viewModel.subTaskTemplateId && (
                  <div className="form-group">
                    <div className="col-md-offset-4 col-md-8">
                      {intl.formatMessage(
                        messages.TheDeadlineOfTheSubtasksWillBeAdjustedAsConfiguredInTaskTemplate,
                      )}
                    </div>
                  </div>
                )}
              <TaskTemplateFilter
                showValidation={showValidation}
                mainTaskTemplateId={viewModel.mainTaskTemplateId}
                subTaskTemplateId={viewModel.subTaskTemplateId}
                showSubtaskFilter={showSubtaskDetails}
                onMainTaskTemplateChanged={e =>
                  this.setState({
                    viewModel: {
                      ...viewModel,
                      mainTaskTemplateId: e,
                      subTaskTemplateId: null,
                    },
                  })
                }
                onSubTaskTemplateChanged={e =>
                  this.setState({
                    viewModel: {
                      ...viewModel,
                      subTaskTemplateId: e,
                    },
                  })
                }
              />

              {showSubtaskDetails && !isPitMandatesTaxReturnTypeTaskflow && (
                <React.Fragment>
                  <div className="form-group">
                    <label className="control-label col-md-4">
                      {intl.formatMessage(messages.NewTaskStatus)}
                    </label>
                    <div className="col-md-8">
                      <select
                        onChange={e =>
                          this.setState({
                            viewModel: {
                              ...viewModel,
                              desiredStatus: e.target.value,
                            },
                          })
                        }
                        className="form-control"
                        value={viewModel.desiredStatus}
                      >
                        <option value=""> {intl.formatMessage(messages.DontChangeStatus)} </option>
                        <option value={0}>
                          {intl.formatMessage(listItemMsg('taskstatus', 0, 'To do'))}
                        </option>
                        <option value={1}>
                          {intl.formatMessage(listItemMsg('taskstatus', 1, 'In progress'))}
                        </option>
                        <option value={2}>
                          {intl.formatMessage(listItemMsg('taskstatus', 2, 'Done'))}
                        </option>
                      </select>
                    </div>
                  </div>
                  {viewModel.subTaskTemplateId && (
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.MarkAsBlocked)}
                      </label>
                      <div className="col-md-8 margin5T">
                        <Checkbox
                          onClick={value =>
                            this.setState({
                              viewModel: {
                                ...viewModel,
                                markAsBlocked: value,
                              },
                            })
                          }
                          value={viewModel.markAsBlocked}
                        />
                      </div>
                    </div>
                  )}

                  {viewModel.subTaskTemplateId && (
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.MarkAsInapplicable)}
                      </label>
                      <div className="col-md-8 margin5T">
                        <Checkbox
                          onClick={value =>
                            this.setState({
                              viewModel: {
                                ...viewModel,
                                markAsInapplicable: value,
                              },
                            })
                          }
                          value={viewModel.markAsInapplicable}
                        />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )}
              {showReferenceDate ? (
                <React.Fragment>
                  <div className="form-group">
                    <label className="control-label col-md-4">
                      {intl.formatMessage(messages.ReferenceDate)}
                    </label>
                    <label className="col-md-8">
                      {intl.formatMessage(messages.LastDayOfVatPeriod)}
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="control-label col-md-8">
                      {intl.formatMessage(messages.DaysBefore)}
                    </label>
                    <div className="col-md-2">
                      <input
                        type="number"
                        autoFocus
                        value={viewModel.toleranceDaysBefore}
                        className="form-control"
                        onChange={e =>
                          this.setState({
                            viewModel: {
                              ...viewModel,
                              toleranceDaysBefore: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="control-label col-md-8">
                      {intl.formatMessage(messages.DaysAfter)}
                    </label>
                    <div className="col-md-2">
                      <input
                        type="number"
                        autoFocus
                        value={viewModel.toleranceDaysAfter}
                        className="form-control"
                        onChange={e =>
                          this.setState({
                            viewModel: {
                              ...viewModel,
                              toleranceDaysAfter: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </React.Fragment>
              ) : null}
              {viewModel.workflowType === 58 && (
                <div className="form-group">
                  <div className="col-md-offset-4 col-md-8">
                    {intl.formatMessage(messages.GeneratedDocumentQrCodeScannedInfo)}
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(WorkflowConfigurationModal);
